import { Tooltip, VisuallyHidden } from "@mantine/core";
import { useId, type ReactNode } from "react";

import classes from "./Soon.module.css";

/** What the control under `Soon` takes: disabled to a screen reader, described as coming soon. */
export interface SoonProps {
  "aria-disabled": true;
  "aria-describedby": string;
}

/**
 * A control the live app has and this build does not yet (design-system.md decisions 23 and 64):
 * it keeps the live control's look, stays focusable, and has "Coming soon" as its description and
 * as a tooltip on hover and on focus. Not `disabled`, and no `data-disabled`: either would draw
 * Mantine's grey disabled look, and a disabled control takes no focus and no hover, so no one
 * learns why. The control gets no handler, so it does nothing.
 */
export function Soon(props: { children: (props: SoonProps) => ReactNode; block?: boolean }) {
  const { children, block = false } = props;
  const id = useId();
  return (
    <Tooltip label="Coming soon" events={{ hover: true, focus: true, touch: false }}>
      {/* `block` spans the row, for a whole field rather than a button. */}
      <span className={classes.soon} data-block={block || undefined}>
        {children({ "aria-disabled": true, "aria-describedby": id })}
        <VisuallyHidden id={id}>Coming soon</VisuallyHidden>
      </span>
    </Tooltip>
  );
}
